import { useEffect, useState } from "react";
import { Eye, Users, Clock, MousePointerClick, BookOpenCheck, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Row {
  slug: string;
  views: number;
  unique_readers: number;
  avg_time_on_page_s: number | null;
  max_time_on_page_s: number | null;
  avg_scroll_depth_pct: number | null;
  read_completions: number;
}

const fmtTime = (s: number | null) => {
  if (s == null) return "N/A";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
};

const BlogAnalyticsTab = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase.rpc("blog_article_stats");
    if (err) setError(err.message);
    else setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totals = rows.reduce(
    (acc, r) => ({
      views: acc.views + Number(r.views),
      readers: acc.readers + Number(r.unique_readers),
      completions: acc.completions + Number(r.read_completions),
    }),
    { views: 0, readers: 0, completions: 0 }
  );

  const card = "rounded-lg border p-4";
  const cardStyle = { background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" };
  const labelStyle = { color: "hsl(0 0% 55%)" };
  const valueStyle = { color: "hsl(0 0% 96%)" };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: "hsl(0 0% 96%)" }}>Blog Reading Analytics</h3>
          <p className="text-xs mt-0.5" style={labelStyle}>
            Views, unique readers, time on page, and scroll depth per article.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border"
          style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 70%)" }}
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className={card} style={cardStyle}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider" style={labelStyle}>
            <Eye size={12} /> Total views
          </div>
          <div className="mt-1 text-2xl font-bold" style={valueStyle}>{totals.views}</div>
        </div>
        <div className={card} style={cardStyle}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider" style={labelStyle}>
            <Users size={12} /> Unique readers
          </div>
          <div className="mt-1 text-2xl font-bold" style={valueStyle}>{totals.readers}</div>
        </div>
        <div className={card} style={cardStyle}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider" style={labelStyle}>
            <BookOpenCheck size={12} /> Full reads
          </div>
          <div className="mt-1 text-2xl font-bold" style={valueStyle}>{totals.completions}</div>
        </div>
        <div className={card} style={cardStyle}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider" style={labelStyle}>
            <MousePointerClick size={12} /> Articles tracked
          </div>
          <div className="mt-1 text-2xl font-bold" style={valueStyle}>{rows.length}</div>
        </div>
      </div>

      {error && (
        <p className="text-sm" style={{ color: "hsl(0 70% 65%)" }}>Could not load stats: {error}</p>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm" style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 55%)" }}>
          No reading data yet. Stats appear here as soon as visitors open your blog articles.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "hsl(0 0% 14%)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider" style={{ color: "hsl(0 0% 50%)", background: "hsl(0 0% 5%)" }}>
                <th className="px-4 py-2.5 font-medium">Article</th>
                <th className="px-4 py-2.5 font-medium text-right">Views</th>
                <th className="px-4 py-2.5 font-medium text-right">Readers</th>
                <th className="px-4 py-2.5 font-medium text-right">Avg time</th>
                <th className="px-4 py-2.5 font-medium text-right">Longest read</th>
                <th className="px-4 py-2.5 font-medium text-right">Avg scroll</th>
                <th className="px-4 py-2.5 font-medium text-right">Full reads</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} className="border-t" style={{ borderColor: "hsl(0 0% 12%)" }}>
                  <td className="px-4 py-2.5 max-w-[320px]">
                    <a href={`/blog/${r.slug}`} target="_blank" rel="noopener noreferrer"
                      className="hover:underline" style={{ color: "hsl(42 60% 80%)" }}>
                      {r.slug.replace(/-/g, " ")}
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>{r.views}</td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>{r.unique_readers}</td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      <Clock size={11} style={labelStyle} />{fmtTime(r.avg_time_on_page_s)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>{fmtTime(r.max_time_on_page_s)}</td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>
                    {r.avg_scroll_depth_pct != null ? `${Math.round(r.avg_scroll_depth_pct)}%` : "N/A"}
                  </td>
                  <td className="px-4 py-2.5 text-right" style={valueStyle}>{r.read_completions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogAnalyticsTab;
